import React, { useState, useEffect, useRef } from 'react';
import {
  DollarSign,
  CreditCard,
  Calculator,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  FileText,
  Eye,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Bookmark,
  Star,
  Share,
  Info,
  ExternalLink,
  Archive,
  Send,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Activity,
  Zap,
  Shield,
  Lock,
  Key,
  Users,
  Building,
  Calendar,
  Phone,
  Mail,
  MapPin,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Settings,
  Link,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Database,
  Layers,
  User,
  Languages,
  Mic,
  Play,
  Pause,
  Square,
  RefreshCw,
  Copy,
  Download,
  Globe,
  Receipt,
  Users2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Target,
  Brain,
  MessageSquare,
  AlertCircle,
  CheckSquare as CheckSquareIcon,
  HelpCircle,
  Package,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast } from '../common/Toast';
import { assetFinanceService } from '../../services/assetFinanceService';
import { assetService } from '../../services/assetService';

// Custom Dropdown Component for Loan Actions
const LoanActionDropdown = ({
  loan,
  onView,
  onEdit,
  onMakePayment,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close the dropdown when the user scrolls or resizes anywhere on the page
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => {
      setIsOpen(false);
    };

    // Use capture on scroll so scrolling inside nested containers is caught
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('wheel', handleScrollOrResize, {
      passive: true,
      capture: true,
    });
    window.addEventListener('touchmove', handleScrollOrResize, {
      passive: true,
      capture: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('wheel', handleScrollOrResize, true);
      window.removeEventListener('touchmove', handleScrollOrResize, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 200;

      setOpenUpward(spaceBelow < menuHeight);
      // Compute menu position using fixed positioning so it won't be clipped
      const menuWidth = 180;
      const padding = 8;
      const top =
        spaceBelow < menuHeight
          ? Math.max(buttonRect.top - menuHeight, padding)
          : Math.min(buttonRect.bottom, viewportHeight - padding - menuHeight);
      // Right align the menu to the button's right edge when possible
      let left = buttonRect.right - menuWidth;
      if (left < padding) left = padding;
      if (left + menuWidth > window.innerWidth - padding)
        left = window.innerWidth - menuWidth - padding;
      setMenuStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
        minWidth: `${menuWidth}px`,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (loan && (loan.id || loan._id)) {
      onView(loan);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (loan && (loan.id || loan._id)) {
      onEdit(loan);
    }
  };

  const handleMakePayment = () => {
    setIsOpen(false);
    if (loan && (loan.id || loan._id)) {
      onMakePayment(loan);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (loan && (loan.id || loan._id)) {
      onDelete(loan);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen && (
        <ul
          className="dropdown-menu dropdown-menu-end show"
          style={{
            // use computed fixed positioning to avoid clipping by parent containers
            ...(menuStyle || {
              position: 'fixed',
              zIndex: 9999,
              minWidth: '180px',
            }),
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: '4px 0 0 0',
            overflow: 'hidden',
          }}
        >
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleView}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Eye
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              View Loan
            </button>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleEdit}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Edit
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              Edit Loan
            </button>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleMakePayment}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <CreditCard
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              Make Payment
            </button>
          </li>
          <li
            style={{
              borderTop: '1px solid #e5e7eb',
              marginTop: '4px',
              paddingTop: '4px',
            }}
          >
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#dc2626',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fef2f2';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2
                size={16}
                style={{ marginRight: '10px', color: '#dc2626', flexShrink: 0 }}
              />
              Delete Loan
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

const AssetFinance = () => {
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState('loans');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [assetLoans, setAssetLoans] = useState([]);
  const [assets, setAssets] = useState([]);
  const [financingOptions, setFinancingOptions] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);

  const stats = {
    totalLoans: analytics?.totalLoans || 0,
    totalOutstanding: analytics?.totalOutstanding || 0,
    totalMonthlyPayments: analytics?.totalMonthlyPayments || 0,
    averageInterestRate: analytics?.averageInterestRate || 0,
    activeLoans: analytics?.activeLoans || 0,
    currentPayments: analytics?.currentPayments || 0,
    totalAssetValue: analytics?.totalAssetValue || 0,
    availableOptions: financingOptions.length,
  };

  const tabs = [
    { id: 'loans', label: 'Asset Loans' },
    { id: 'options', label: 'Financing Options' },
    { id: 'payments', label: 'Payment Schedule' },
    { id: 'analytics', label: 'Financial Analytics' },
    { id: 'reports', label: 'Reports' },
  ];

  const formatCurrency = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      value || 0,
    );

  const loadLoans = async () => {
    try {
      setLoading(true);
      const response = await assetFinanceService.getAssetLoans();
      if (response.success) {
        setAssetLoans(response.data.loans || []);
        if (!selectedLoanId && response.data.loans?.length) {
          setSelectedLoanId(response.data.loans[0]._id);
        }
      }
    } catch (err) {
      showError(err.message || 'Failed to load asset loans');
    } finally {
      setLoading(false);
    }
  };

  const loadFinancingOptions = async () => {
    try {
      setLoading(true);
      const response = await assetFinanceService.getFinancingOptions();
      if (response.success) {
        setFinancingOptions(response.data || []);
      }
    } catch (err) {
      showError(err.message || 'Failed to load financing options');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentSchedule = async (loanId) => {
    if (!loanId) {
      setPaymentSchedule([]);
      return;
    }
    try {
      setLoading(true);
      const response = await assetFinanceService.getPaymentSchedule(loanId);
      if (response.success) {
        setPaymentSchedule(response.data || []);
      }
    } catch (err) {
      showError(err.message || 'Failed to load payment schedule');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await assetFinanceService.getAnalytics();
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err) {
      showError(err.message || 'Failed to load analytics');
    }
  };

  const extractAssetList = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.assets)) return payload.assets;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const loadAssets = async () => {
    try {
      const response = await assetService.getAssets({ limit: 100, page: 1 });
      const payload = response?.data?.data || response?.data || response;
      setAssets(extractAssetList(payload));
    } catch (err) {
      showError(err.message || 'Failed to load assets');
    }
  };

  useEffect(() => {
    loadLoans();
    loadFinancingOptions();
    loadAnalytics();
    loadAssets();
  }, []);

  useEffect(() => {
    if (showLoanModal) {
      loadAssets();
    }
  }, [showLoanModal]);

  useEffect(() => {
    if (activeTab === 'payments') {
      loadPaymentSchedule(selectedLoanId);
    }
  }, [activeTab, selectedLoanId]);

  // Define columns for Asset Loans DataTable
  const loanColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'assetName',
      label: 'Asset & Loan Details',
      width: '300px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.loanType || 'Standard'} • {row.lender} •{' '}
            {row.asset?.assetCode || row.asset?._id}
          </div>
        </div>
      ),
    },
    {
      key: 'loanAmount',
      label: 'Loan Amount',
      width: '120px',
      render: (amount) => (
        <span style={{ fontWeight: '600', color: '#1f2937' }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      key: 'outstandingBalance',
      label: 'Outstanding',
      width: '120px',
      render: (balance) => (
        <span style={{ fontWeight: '600', color: '#dc2626' }}>
          {formatCurrency(balance)}
        </span>
      ),
    },
    {
      key: 'interestRate',
      label: 'Interest Rate',
      width: '100px',
      render: (rate) => (
        <span style={{ fontWeight: '500', color: '#059669' }}>
          {rate?.toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'monthlyPayment',
      label: 'Monthly Payment',
      width: '120px',
      render: (payment) => (
        <span style={{ fontWeight: '600', color: '#1f2937' }}>
          {formatCurrency(payment)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      render: (status, row) => {
        const statusConfig = {
          Active: {
            color: '#10b981',
            bg: '#d1fae5',
            icon: <CheckCircle size={14} />,
          },
          'Paid Off': {
            color: '#6b7280',
            bg: '#f3f4f6',
            icon: <CheckCircle size={14} />,
          },
          Defaulted: {
            color: '#ef4444',
            bg: '#fee2e2',
            icon: <XCircle size={14} />,
          },
        };
        const config = statusConfig[status] || statusConfig.Active;

        return (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: config.bg,
              color: config.color,
              width: 'fit-content',
            }}
          >
            {config.icon}
            {status}
          </span>
        );
      },
    },
    {
      key: 'nextPaymentDate',
      label: 'Next Payment',
      width: '120px',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => {
        if (!row || (!row.id && !row._id)) {
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <LoanActionDropdown
            loan={row}
            onView={handleView}
            onEdit={handleEdit}
            onMakePayment={handleMakePayment}
            onDelete={handleDelete}
          />
        );
      },
    },
  ];

  // Define columns for Financing Options DataTable
  const optionColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'assetType',
      label: 'Financing Option',
      width: '250px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.financingType} • {row.provider}
          </div>
        </div>
      ),
    },
    {
      key: 'interestRateRange',
      label: 'Rate Range',
      width: '140px',
      render: (range) => (
        <span style={{ fontWeight: '600', color: '#059669' }}>
          {range?.min?.toFixed(2)}% - {range?.max?.toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'termRange',
      label: 'Term (Months)',
      width: '100px',
      render: (term) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>
          {term?.min} - {term?.max}
        </span>
      ),
    },
    {
      key: 'amountRange',
      label: 'Amount Range',
      width: '160px',
      render: (range) => (
        <span style={{ fontWeight: '600', color: '#1f2937' }}>
          {formatCurrency(range?.min)} - {formatCurrency(range?.max)}
        </span>
      ),
    },
    {
      key: 'averageLtv',
      label: 'Avg LTV',
      width: '80px',
      render: (ltv) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>
          {ltv ? `${ltv.toFixed(1)}%` : '—'}
        </span>
      ),
    },
    {
      key: 'provider',
      label: 'Provider',
      width: '160px',
      render: (provider) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>{provider}</span>
      ),
    },
  ];

  const scheduleColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'installment',
      label: 'Installment',
      width: '110px',
    },
    {
      key: 'date',
      label: 'Payment Date',
      width: '140px',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      key: 'payment',
      label: 'Payment',
      width: '120px',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'principal',
      label: 'Principal',
      width: '120px',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'interest',
      label: 'Interest',
      width: '120px',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'balance',
      label: 'Balance',
      width: '120px',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => (
        <span
          style={{
            fontWeight: '500',
            color: status === 'Paid' ? '#059669' : '#374151',
          }}
        >
          {status}
        </span>
      ),
    },
  ];

  const [loanFormData, setLoanFormData] = useState({
    assetId: '',
    loanAmount: '',
    interestRate: '',
    loanTerm: '',
    lender: '',
    startDate: '',
    loanType: '',
    collateralType: '',
  });
  const [editingLoanId, setEditingLoanId] = useState(null);

  // Handler functions
  const handleView = (item) => {
    if (item?._id) {
      setSelectedLoanId(item._id);
      setActiveTab('payments');
      success('Viewing payment schedule for loan');
    }
  };

  const handleEdit = (loan) => {
    setIsProcessing(false);
    setEditingLoanId(loan?._id || null);
    setLoanFormData({
      assetId: loan?.asset?._id || loan?.asset || '',
      loanAmount: loan?.loanAmount || '',
      interestRate: loan?.interestRate || '',
      loanTerm: loan?.loanTerm || '',
      lender: loan?.lender || '',
      startDate: loan?.startDate
        ? new Date(loan.startDate).toISOString().split('T')[0]
        : '',
      loanType: loan?.loanType || '',
      collateralType: loan?.collateralType || '',
    });
    setShowLoanModal(true);
    success('Opening loan editor');
  };

  const handleMakePayment = (loan) => {
    setSelectedItem(loan);
    setShowPaymentModal(true);
    success('Opening payment form');
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleRefresh = () => {
    loadLoans();
    loadFinancingOptions();
    loadAnalytics();
  };

  const handleHelpClick = () => {
    const helpUrl = '/help/asset-finance';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no',
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  const handleCreateLoan = () => {
    setIsProcessing(false);
    setEditingLoanId(null);
    setLoanFormData({
      assetId: '',
      loanAmount: '',
      interestRate: '',
      loanTerm: '',
      lender: '',
      startDate: '',
      loanType: '',
      collateralType: '',
    });
    setShowLoanModal(true);
  };

  const handleLoanSubmit = async (event) => {
    event.preventDefault();
    console.log('SUBMIT CLICKED');
    setIsProcessing(true);
    try {
      // Basic client-side validation: ensure selected assetId is a valid Mongo ObjectId
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!loanFormData.assetId || !objectIdRegex.test(loanFormData.assetId)) {
        setIsProcessing(false);
        showError('Please select a valid asset before saving the loan');
        return;
      }
      const payload = {
        asset: loanFormData.assetId,
        loanAmount: Number(loanFormData.loanAmount),
        interestRate: Number(loanFormData.interestRate),
        loanTerm: Number(loanFormData.loanTerm),
        lender: loanFormData.lender,
        startDate: loanFormData.startDate,
        loanType: loanFormData.loanType,
        collateralType: loanFormData.collateralType,
      };

      if (editingLoanId) {
        await assetFinanceService.updateAssetLoan(editingLoanId, payload);
        success('Asset loan updated successfully');
      } else {
        await assetFinanceService.createAssetLoan(payload);
        success('Asset loan created successfully');
      }

      setShowLoanModal(false);
      setEditingLoanId(null);
      await loadLoans();
      await loadAnalytics();
    } catch (error) {
      showError(error.message || 'Failed to save asset loan');
    } finally {
      setIsProcessing(false);
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    {
      label: 'New Loan',
      icon: <Plus size={18} />,
      onClick: handleCreateLoan,
      variant: 'primary',
      tooltip: 'Create a new loan',
    },
  ];

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (selectedItem?._id) {
        await assetFinanceService.deleteAssetLoan(selectedItem._id);
        await loadLoans();
        success('Asset loan deleted successfully');
      }
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Failed to delete loan');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePaymentConfirm = async () => {
    setIsProcessing(true);
    try {
      if (selectedItem?._id) {
        const amount =
          selectedItem.monthlyPayment || selectedItem.outstandingBalance;
        await assetFinanceService.processPayment({
          loanId: selectedItem._id,
          amount,
        });
        await loadLoans();
        await loadPaymentSchedule(selectedItem._id);
        success('Payment processed successfully');
      }
      setShowPaymentModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Failed to process payment');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Asset Finance"
        description="Comprehensive financial management for asset loans, financing options, and payment tracking"
        actions={headerActions}
      />
      <div className="page-container">
        {/* KPI Cards Grid – StatCard reference styling */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {/* Total Outstanding */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +5.2%
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(stats.totalOutstanding)}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Outstanding
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Payments */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +8.1%
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <CreditCard size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(stats.totalMonthlyPayments)}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Monthly Payments
                </div>
              </div>
            </div>
          </div>

          {/* Active Loans */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +12.3%
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Activity size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.activeLoans}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Active Loans
                </div>
              </div>
            </div>
          </div>

          {/* Avg. Interest Rate */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +0.3%
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <TrendingUp size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.averageInterestRate.toFixed(2)}%
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Avg. Interest Rate
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 border border-slate-200">
          <div className="w-full flex items-center bg-white border-b-1 border-slate-200">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center justify-center gap-2 px-5 py-3 w-full
                    text-sm font-medium
                    border-b-2 transition-colors duration-200
                    ${
                      isActive
                        ? '!border-b-2 border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                        : 'border-transparent text-[var(--text-secondary)] hover:bg-slate-100 text-slate-500'
                    }
                  `}
                >
                  {tab.id === 'loans' && (
                    <CreditCard
                      size={18}
                      className={
                        isActive
                          ? 'text-[var(--primary-color)]'
                          : 'text-slate-400'
                      }
                    />
                  )}
                  {tab.id === 'options' && (
                    <Calculator
                      size={18}
                      className={
                        isActive
                          ? 'text-[var(--primary-color)]'
                          : 'text-slate-400'
                      }
                    />
                  )}
                  {tab.id === 'payments' && (
                    <Receipt
                      size={18}
                      className={
                        isActive
                          ? 'text-[var(--primary-color)]'
                          : 'text-slate-400'
                      }
                    />
                  )}
                  {tab.id === 'analytics' && (
                    <BarChart3
                      size={18}
                      className={
                        isActive
                          ? 'text-[var(--primary-color)]'
                          : 'text-slate-400'
                      }
                    />
                  )}
                  {tab.id === 'reports' && (
                    <FileText
                      size={18}
                      className={
                        isActive
                          ? 'text-[var(--primary-color)]'
                          : 'text-slate-400'
                      }
                    />
                  )}
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Asset Loans Tab */}
        {activeTab === 'loans' && (
          <div>
            {/* Loans DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={assetLoans}
                columns={loanColumns}
                title={`Asset Loans (${assetLoans.length} loans)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Financing Options Tab */}
        {activeTab === 'options' && (
          <div>
            {/* Options DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={financingOptions}
                columns={optionColumns}
                title={`Financing Options (${financingOptions.length} options)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Other Tabs */}
        {activeTab === 'payments' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <label style={{ fontWeight: 500, color: '#374151' }}>
                Select Loan
              </label>
              <select
                value={selectedLoanId || ''}
                onChange={(event) => setSelectedLoanId(event.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  minWidth: '280px',
                }}
              >
                {assetLoans.map((loan) => (
                  <option key={loan._id} value={loan._id}>
                    {loan.assetName} • {loan.loanId}
                  </option>
                ))}
              </select>
            </div>
            <DataTable
              data={paymentSchedule}
              columns={scheduleColumns}
              title={`Payment Schedule (${paymentSchedule.length} entries)`}
              loading={loading}
              searchable={false}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={false}
            />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Outstanding
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(stats.totalOutstanding)}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Monthly Payments
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(stats.totalMonthlyPayments)}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Average Interest Rate
                </div>
                <div style={{ fontWeight: 600 }}>
                  {stats.averageInterestRate.toFixed(2)}%
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Asset Value
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(stats.totalAssetValue)}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Active Loans
                </div>
                <div style={{ fontWeight: 600 }}>{stats.activeLoans}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Current Payments
                </div>
                <div style={{ fontWeight: 600 }}>{stats.currentPayments}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Loans
                </div>
                <div style={{ fontWeight: 600 }}>{stats.totalLoans}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Depreciation (Assets)
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(
                    analytics?.depreciationSummary?.totalDepreciation || 0,
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showLoanModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050,
              padding: '20px',
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setShowLoanModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                width: '100%',
                maxWidth: '520px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                {editingLoanId ? 'Edit Asset Loan' : 'Create Asset Loan'}
              </h3>
              <form onSubmit={handleLoanSubmit}>
                {/* Form Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px',
                    marginTop: '12px',
                  }}
                >
                  {/* Asset ID (select from loaded assets to ensure valid id) */}
                  <div>
                    <label className="form-label">Asset</label>
                    <select
                      value={loanFormData.assetId}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          assetId: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    >
                      <option value="">-- Select asset --</option>
                      {assets && assets.length > 0 ? (
                        assets.map((a) => (
                          <option key={a._id || a.id} value={a._id || a.id}>
                            {a.name || a.assetCode || a._id || a.id}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No assets loaded
                        </option>
                      )}
                    </select>
                  </div>

                  {/* Lender */}
                  <div>
                    <label className="form-label">Lender</label>
                    <input
                      type="text"
                      value={loanFormData.lender}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          lender: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  {/* Loan Amount */}
                  <div>
                    <label className="form-label">Loan Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={loanFormData.loanAmount}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          loanAmount: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  {/* Interest Rate */}
                  <div>
                    <label className="form-label">Interest Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={loanFormData.interestRate}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          interestRate: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  {/* Loan Term */}
                  <div>
                    <label className="form-label">Loan Term (Months)</label>
                    <input
                      type="number"
                      value={loanFormData.loanTerm}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          loanTerm: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      value={loanFormData.startDate}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          startDate: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  {/* Loan Type */}
                  <div>
                    <label className="form-label">Loan Type</label>
                    <input
                      type="text"
                      value={loanFormData.loanType}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          loanType: e.target.value,
                        })
                      }
                      className="form-input"
                    />
                  </div>

                  {/* Collateral Type */}
                  <div>
                    <label className="form-label">Collateral Type</label>
                    <input
                      type="text"
                      value={loanFormData.collateralType}
                      onChange={(e) =>
                        setLoanFormData({
                          ...loanFormData,
                          collateralType: e.target.value,
                        })
                      }
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '24px',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '16px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowLoanModal(false)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#ffffff',
                      fontWeight: 500,
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isProcessing}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'var(--primary-color)',
                      color: '#ffffff',
                      fontWeight: 600,
                      opacity: isProcessing ? 0.7 : 1,
                    }}
                  >
                    {editingLoanId ? 'Update Loan' : 'Create Loan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Item"
          message={`Are you sure you want to delete "${
            selectedItem?.assetName || selectedItem?.assetType
          }"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        {/* Payment Modal */}
        <ConfirmationModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onConfirm={handlePaymentConfirm}
          title="Make Payment"
          message={`Process payment for "${
            selectedItem?.assetName
          }"? Monthly payment: ${formatCurrency(selectedItem?.monthlyPayment)}`}
          confirmText="Process Payment"
          cancelText="Cancel"
          type="success"
          isLoading={isProcessing}
        />
      </div>
    </>
  );
};

export default AssetFinance;

// .form-label {
//   font-size: 13px;
//   font-weight: 500;
//   color: #374151;
//   margin-bottom: 6px;
//   display: block;
// }

// .form-input {
//   width: 100%;
//   padding: 10px 12px;
//   border-radius: 8px;
//   border: 1px solid #e5e7eb;
//   font-size: 14px;
//   font-family: var(--font-family);
// }

// .form-input:focus {
//   outline: none;
//   border-color: var(--primary-color);
//   box-shadow: 0 0 0 2px rgba(58, 141, 204, 0.15);
// }
